import { Theme } from '@mui/material/styles'

import { stripTextHover } from '../hover'
import { layout, typography as typeTokens } from '../tokens.generated'
import { FOCUS_VISIBLE } from './Button'

// ----------------------------------------------------------------------
// A tab is a 42px control: `layout.tabHeight`, the dense control inset on each
// side, no block padding, and a glyph beside its label rather than stacked
// over it. Height plus centring is what puts the label on the baseline; a
// control with both a height and a vertical padding is off-centre by whatever
// the two disagree about.
//
// The strip does not draw its own bottom line. `ResponsiveTabs` owns the
// hairline under the tabs, and the one other strip wraps itself in a bordered
// box, so a border here would double the line on every detail page.
//
// The label runs at medium weight and the selected tab steps up to semibold,
// read from the token weights. `theme.typography.fontWeightMedium` is 600 on
// this theme, not 500, so it is the wrong name for this.
//
// A tab is a text control, so it answers the pointer the way text does: it
// underlines and steps its ink. MUI ships no hover on a tab at all, which left
// every detail page in this console with a silent strip of eight to ten
// pressables across the top of it. The selected one answers too: it is still
// something you can press, and a strip where exactly one tab ignores the mouse
// is the inconsistency this rule exists to remove. It steps to the accent's
// own dark, because it is already carrying the accent.
//
// The keyboard gets the same answer and no ring (`stripTextHover`). The strip
// scrolls, so its scroller cut a focused tab's ring to two bars either side
// of the word, a border that read as part of the selection (2026-11-01).
// ----------------------------------------------------------------------

/**
 * The selected indicator is a 2px rule. No token carries the thickness of a
 * selection indicator yet; it is named here so the number appears once.
 */
const INDICATOR_THICKNESS = 2

export default function Tabs(theme: Theme) {
  return {
    MuiTabs: {
      defaultProps: {
        // Eight tabs at 42px scroll rather than wrap.
        variant: 'scrollable' as const,
        allowScrollButtonsMobile: true,
      },
      styleOverrides: {
        root: {
          minHeight: layout.tabHeight,
        },
        indicator: {
          height: INDICATOR_THICKNESS,
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          'minHeight': layout.tabHeight,
          'minWidth': 0,
          'paddingInline': layout.controlInsetSmall,
          'paddingBlock': 0,
          'textTransform': 'none' as const,
          'fontSize': theme.typography.pxToRem(typeTokens.console.body.size),
          'fontWeight': typeTokens.weights.medium,
          'flexDirection': 'row' as const,
          'gap': layout.controlGap,
          // `gap` owns the space between glyph and label now.
          '& .MuiTab-iconWrapper': {
            margin: 0,
          },
          // The selected tab states its own answer at a weight the general
          // one cannot reach. `&.Mui-selected` alone is the same weight as
          // `&:hover`, so which of the two painted a hovered selected tab
          // would be decided by source order and by nothing a reader can see.
          ...stripTextHover(theme, theme.palette.text.primary),
          '&.Mui-selected': {
            'color': theme.palette.primary.main,
            'fontWeight': typeTokens.weights.semibold,
            '&:hover': {
              color: theme.palette.primary.dark,
            },
            [FOCUS_VISIBLE]: {
              color: theme.palette.primary.dark,
            },
          },
        },
        textColorInherit: {
          opacity: 1,
          color: theme.palette.text.secondary,
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTabScrollButton: {
      styleOverrides: {
        root: {
          width: layout.tabHeight,
          borderRadius: '50%',
        },
      },
    },
  }
}
